import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../gql/api';
import formatWorkspaceFromUserRoles from 'src/utilities/formatWorkspaceFromUserRole';
import { PATH_PAGE } from 'src/routes/paths';
import history from 'src/routes/history';
import errorHandler from 'src/gql/errorHandler';
import { setCurrentWorkspace } from '../slices/currentWorkspace';
import { setUser } from '../slices/user';
import { setCustomers } from '../slices/customers';
import { setRoles } from '../slices/roles';
import { setWorkspaces } from '../slices/workspaces';
import { setDevices } from '../slices/devices';
import { loadDefaultData } from './currentWorkspace';
import { setCustomerMode, stopLoading } from '../slices/ui';
import { getCurrentUserInvitations } from './invitations';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { REPORTER_ROLES_BY_RESOURCE } from 'src/constants';

export const loadCustomerDevices = createAsyncThunk(
  'users/loadCustomerDevices',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const userRoles = Object.values(state.roles.entities);

    const devices = [];

    const seenIds = new Set();

    for (const userRole of userRoles) {
      if (userRole.customer?.devices) {
        for (const device of userRole.customer.devices) {
          if (!seenIds.has(device.id)) {
            devices.push(device);
            seenIds.add(device.id);
          }
        }
      } else if (userRole.device) {
        if (!seenIds.has(userRole.device.id)) {
          devices.push(userRole.device);
          seenIds.add(userRole.device.id);
        }
      }
    }
    if (devices.length > 0) {
      dispatch(setDevices(devices));
    }

    return devices;
  }
);

export const getCurrentUser = createAsyncThunk(
  'users/getCurrentUser',
  async (_payload, { dispatch }) => {
    try {
      const currentUser = await api.user.getCurrentUser();
      if (currentUser?.data?.getCurrentUser) {
        const { userRoles, ...user } = currentUser.data.getCurrentUser;

        dispatch(setUser({ ...user }));
        dispatch(setRoles(userRoles));

        const workspaces = formatWorkspaceFromUserRoles(userRoles);
        dispatch(setWorkspaces(workspaces));

        const currentWorkspace = workspaces.find(
          (workspace) =>
            workspace.id === currentUser.data?.getCurrentUser?.currentWorkspace
        );

        if (currentWorkspace) {
          dispatch(setCurrentWorkspace(currentWorkspace.id || null));

          // TODO: refactorizar ESTO IMPORTANTE!!! no hacer el fetch aqui
          await dispatch(loadDefaultData(currentWorkspace.id)).unwrap();
        }
        const userInvitations = await dispatch(
          getCurrentUserInvitations()
        ).unwrap();

        const customerMember = await userRoles?.reduce((acc, currentValue) => {
          if (currentValue.customer !== null) {
            acc.push(currentValue.customer);
          } else if (currentValue.device?.customer) {
            acc.push(currentValue.device.customer);
          }

          return acc;
        }, []);

        dispatch(setCustomers(customerMember));
        if (
          (workspaces.length === 0 ||
            window.location.pathname === '/customer' ||
            window.location.pathname.startsWith('/customer/')) &&
          userInvitations.length === 0 &&
          userRoles.some(
            (role) =>
              role.customer ||
              (role.device &&
                role.role.id !== REPORTER_ROLES_BY_RESOURCE.DEVICE)
          )
        ) {
          dispatch(setCustomerMode());
        }
        dispatch(stopLoading());
      }
    } catch (e) {
      dispatch(stopLoading());
      history.push(PATH_PAGE.logout);
      throw e;
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async (payload, { dispatch }) => {
    const { data, onSuccess, onFailure, setLoading } = payload;
    try {
      const response = await api.user.updateUser({ data });
      if (response?.data?.updateUser.id) {
        dispatch(setUser(response.data.updateUser));
      } else {
        throw new Error('Server error');
      }
      setLoading && setLoading(false);
      onSuccess && onSuccess();
    } catch (error) {
      console.log(error);
      onFailure && onFailure(errorHandler(error));
      setLoading && setLoading(false);
    }
  }
);

export const updateUserConfig = createAsyncThunk(
  'user/updateUserConfig',
  async (payload, { dispatch }) => {
    const { data } = payload;
    try {
      const response = await api.user.updateUser({ data });
      if (response?.data?.updateUser) {
        const currentUser = await api.user.getCurrentUser();
        await dispatch(setUser(currentUser.data.getCurrentUser));
      } else {
        throw new Error('Server error');
      }
    } catch (error) {
      throw new Error('Server error');
    }
  }
);

export const getUserById = createAsyncThunk(
  'user/getUserById',
  async (userId) => {
    const response = await api.user.getUserById({
      userId,
    });

    return response.data.getUserById;
  },
  {
    condition: (userId, { getState }) => {
      const { users } = getState();
      const user = users.entities[userId];

      return !user;
    },
  }
);

export const updateUserProfilePicture = createAsyncThunk(
  'user/updateUserProfilePicture',
  async (payload) => {
    const { file } = payload;
    const pictureName = `${uuidv4()}.webp`;

    const { data } = await api.upload.getUploadUrl({
      filename: pictureName,
    });

    const options = {
      maxWidthOrHeight: 200,
      useWebWorker: true,
      fileType: 'image/webp',
    };

    const compressedImage = await imageCompression(file, options);

    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': compressedImage.type,
      },
      mode: 'cors',
      body: compressedImage,
    };

    const uploadToS3Response = await fetch(
      data?.getUploadSignedUrl?.url,
      requestOptions
    );

    if (uploadToS3Response.ok) {
      return pictureName;
    } else {
      throw new Error('Error uploading image');
    }
  }
);
